// Dialog stack — imperative modal system. See docs/framework/ui/dialogs.md.
//
// The stack is a module-scoped reactive array. `openDialog` pushes an entry and
// returns a promise that resolves when the entry's `close` is called (or when
// it is dismissed via backdrop/Escape and no result is passed). Every entry
// stays mounted for its whole lifetime — only the top is visible — so a
// dialog's reactive state survives while a child dialog is open on top of it.

import {
  inject,
  markRaw,
  readonly,
  shallowRef,
  type Component,
  type InjectionKey,
  type Ref,
} from 'vue'

export interface OpenDialogOptions {
  /** Backdrop click and Escape close the dialog. Default: true. */
  dismissible?: boolean
  /** Visual width variant. Default: 'default'. */
  size?: 'default' | 'wide' | 'narrow'
}

export interface DialogEntry<T = unknown> {
  id: number
  component: Component
  props: Record<string, unknown>
  options: Required<OpenDialogOptions>
  resolve: (value: T | undefined) => void
}

export interface DialogContext {
  id: number
  close: (result?: unknown) => void
}

const OPTION_DEFAULTS: Required<OpenDialogOptions> = {
  dismissible: true,
  size: 'default',
}

// shallowRef because entries hold Vue Component references (already markRaw'd)
// and per-entry resolve functions we do not want proxied. The array is always
// replaced immutably, never mutated in place.
const stack = shallowRef<DialogEntry[]>([])
let nextId = 1

/** Injection key used by DialogEntryHost.vue to hand `close` to a dialog body. */
export const DIALOG_CONTEXT_KEY: InjectionKey<DialogContext> = Symbol('dialog:context')

/**
 * Push a dialog onto the stack. Resolves with whatever the dialog passes to
 * its close() function, or `undefined` on dismiss.
 */
export function openDialog<T = void>(
  component: Component,
  props: Record<string, unknown> = {},
  options: OpenDialogOptions = {},
): Promise<T | undefined> {
  return new Promise<T | undefined>((resolve) => {
    const entry: DialogEntry<T> = {
      id: nextId++,
      // markRaw so Vue does not deeply reactivate the component definition.
      component: markRaw(component),
      props,
      options: { ...OPTION_DEFAULTS, ...options },
      resolve,
    }
    stack.value = [...stack.value, entry as unknown as DialogEntry]
  })
}

/** Close a dialog by id. Idempotent — closing a stale id is a no-op. */
export function closeDialog(id: number, result?: unknown): void {
  const entry = stack.value.find((e) => e.id === id)
  if (!entry) return
  stack.value = stack.value.filter((e) => e.id !== id)
  entry.resolve(result)
}

/** Read the current stack. Consumed by DialogRoot; leaf code has no need. */
export function useDialogStack(): { stack: Readonly<Ref<readonly DialogEntry[]>> } {
  return { stack: readonly(stack) as Readonly<Ref<readonly DialogEntry[]>> }
}

/**
 * Called from inside a dialog body component via provide/inject. Returns the
 * close function bound to this dialog's entry. Throws in dev when used outside
 * a dialog context, since that is always a wiring bug.
 */
export function useDialog<T = void>(): { id: number; close: (result?: T) => void } {
  const ctx = inject(DIALOG_CONTEXT_KEY)
  if (!ctx) {
    throw new Error(
      'useDialog() called outside a dialog context. ' +
      'The component must be opened via openDialog().',
    )
  }
  return {
    id: ctx.id,
    close: (result?: T) => ctx.close(result as unknown),
  }
}

/**
 * Convenience for "are you sure?" prompts. Returns true on Confirm, false on
 * Cancel/dismiss. For more complex confirmation flows, build a dedicated
 * dialog and use openDialog directly.
 */
export function confirmDialog(opts: {
  title:         string
  message:       string
  confirmLabel?: string
  cancelLabel?:  string
  danger?:       boolean
}): Promise<boolean> {
  // Component imported lazily so this module has no circular import with the
  // ConfirmDialog Vue component (which itself imports useDialog).
  return import('./components/ConfirmDialog.vue').then(({ default: ConfirmDialog }) =>
    openDialog<boolean>(ConfirmDialog, {
      title:        opts.title,
      message:      opts.message,
      confirmLabel: opts.confirmLabel ?? 'Confirm',
      cancelLabel:  opts.cancelLabel  ?? 'Cancel',
      danger:       opts.danger       ?? false,
    }, { size: 'narrow' }).then((result) => result === true),
  )
}
